import React, { useState, useEffect } from 'react'
import {
  Box,
  Container,
  Heading,
  Text,
  Flex,
  Badge,
  SimpleGrid,
  Stack,
  useColorModeValue
} from '@chakra-ui/react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'

const MotionBox = motion(Box)

const SimulatedChart = () => {
  // Simple SVG path animation to simulate a chart
  return (
    <Box
      w="full"
      h="300px"
      bg="dark.card"
      borderRadius="xl"
      border="1px solid"
      borderColor="whiteAlpha.100"
      position="relative"
      overflow="hidden"
      boxShadow="2xl"
    >
      {/* Grid Lines */}
      <Box
        position="absolute"
        inset={0}
        bgImage="linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px)"
        bgSize="40px 40px"
      />

      {/* Chart Line */}
      <svg
        viewBox="0 0 1000 300"
        style={{ width: '100%', height: '100%', position: 'absolute' }}
      >
        <motion.path
          d="M0,250 C100,240 150,280 250,200 C350,120 400,180 500,100 C600,20 650,80 750,50 C850,20 900,60 1000,10"
          fill="none"
          stroke="#3b82f6"
          strokeWidth="4"
          initial={{ pathLength: 0, opacity: 0 }}
          whileInView={{ pathLength: 1, opacity: 1 }}
          transition={{ duration: 3, ease: 'easeInOut' }}
        />
        <motion.path
          d="M0,250 C100,240 150,280 250,200 C350,120 400,180 500,100 C600,20 650,80 750,50 C850,20 900,60 1000,10 L1000,300 L0,300 Z"
          fill="url(#gradient)"
          opacity="0.2"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 0.2 }}
          transition={{ duration: 3, delay: 0.5 }}
        />
        <defs>
          <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>

      {/* Floating Badges (Buy/Sell Signals) */}
      <MotionBox
        position="absolute"
        top="30%"
        left="25%"
        initial={{ scale: 0, opacity: 0 }}
        whileInView={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1, type: 'spring' }}
      >
        <Badge
          colorScheme="green"
          fontSize="xs"
          px={2}
          py={1}
          borderRadius="full"
        >
          BUY @ 0.5234
        </Badge>
      </MotionBox>

      <MotionBox
        position="absolute"
        top="15%"
        left="50%"
        initial={{ scale: 0, opacity: 0 }}
        whileInView={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1.8, type: 'spring' }}
      >
        <Badge
          colorScheme="red"
          fontSize="xs"
          px={2}
          py={1}
          borderRadius="full"
        >
          SELL @ 0.5420
        </Badge>
      </MotionBox>

      <MotionBox
        position="absolute"
        top="10%"
        left="75%"
        initial={{ scale: 0, opacity: 0 }}
        whileInView={{ scale: 1, opacity: 1 }}
        transition={{ delay: 2.5, type: 'spring' }}
      >
        <Badge
          colorScheme="green"
          fontSize="xs"
          px={2}
          py={1}
          borderRadius="full"
        >
          BUY @ 0.5380
        </Badge>
      </MotionBox>
    </Box>
  )
}

const StatBox = ({
  label,
  value,
  subtext
}: {
  label: string
  value: string
  subtext: string
}) => (
  <Box textAlign="center">
    <Text color="gray.400" fontSize="sm" mb={1}>
      {label}
    </Text>
    <Heading size="xl" color="white" mb={1}>
      {value}
    </Heading>
    <Text color="brand.400" fontSize="sm">
      {subtext}
    </Text>
  </Box>
)

const PerformanceSection = () => {
  const { t } = useTranslation()

  return (
    <Box
      py={{ base: 20, md: 24 }}
      bg="dark.bg"
      position="relative"
      overflow="hidden"
    >
      {/* Background Decoration */}
      <Box
        position="absolute"
        top="20%"
        right="-10%"
        w="500px"
        h="500px"
        bg="brand.600"
        filter="blur(120px)"
        opacity="0.1"
        borderRadius="full"
      />

      <Container maxW="6xl">
        <SimpleGrid
          columns={{ base: 1, lg: 2 }}
          spacing={{ base: 12, lg: 20 }}
          alignItems="center"
        >
          {/* Left: Text Content */}
          <MotionBox
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Badge
              colorScheme="green"
              mb={4}
              px={3}
              py={1}
              borderRadius="full"
              variant="solid"
              bg="green.500"
              color="white"
            >
              {t('Live Performance')}
            </Badge>
            <Heading
              as="h2"
              size="2xl"
              mb={6}
              lineHeight="1.2"
              bgGradient="linear(to-r, white, gray.400)"
              bgClip="text"
            >
              {t('눈으로 확인하는')}
              <br />
              {t('AI 알고리즘의 정교함')}
            </Heading>
            <Text fontSize="lg" color="gray.400" mb={8} lineHeight="tall">
              {t(
                '웨이브봇의 AI는 24시간 시장을 분석하여 최적의 타이밍을 포착합니다. 감정에 휘둘리지 않는 정교한 알고리즘으로 리스크를 관리하고 안정적인 운용을 지원합니다.'
              )}
            </Text>

            <SimpleGrid columns={3} spacing={6} w="full">
              <StatBox
                label={t('AI 모델 정확도')}
                value="99.2%"
                subtext="Model Confidence"
              />
              <StatBox label={t('리스크 방어율')} value="96.5%" subtext="Downside Protection" />
              <StatBox
                label={t('실시간 데이터 분석')}
                value="24/7"
                subtext="Non-stop Monitoring"
              />
            </SimpleGrid>
          </MotionBox>

          {/* Right: Chart Visual */}
          <MotionBox
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <SimulatedChart />
          </MotionBox>
        </SimpleGrid>
      </Container>
    </Box>
  )
}

export default PerformanceSection
